// apps/spa/src/components/reminders/ReminderHistoryPanel.tsx
// Expanded panel showing the last 10 dispatch audit entries for a reminder.
//
// Mounted by ReminderTable when the chevron is clicked. Calls useReminderHistory(id).
// Displays: status icon / timestamp / #channel-name / outcome (messageId or error).
//
// Status icon derivation (from payload):
//   payload.success === true → ✅
//   payload.retryAfter != null → ⚠️ (rate-limited)
//   otherwise → ❌
//
// Channel name resolved from useChannels() by channelId in payload.channelId.
// Timestamp formatted as 'YYYY-MM-DD HH:mm ET' using BASTN_TIMEZONE.
// No enrichment — 04-03 owns the full enrichment layer.

import { useReminderHistory, type AuditLogRow } from '@/hooks/useReminderHistory';
import { useChannels } from '@/hooks/useChannels';
import { useTimeFormat } from '@/hooks/useTimeFormat';
import { Skeleton } from '@/components/ui/skeleton';
import { BASTN_TIMEZONE } from '@guardian/shared';

interface ReminderHistoryPanelProps {
  reminderId: string;
}

function formatTs(ts: number, hour12: boolean): string {
  return new Date(ts).toLocaleString('en-US', {
    timeZone: BASTN_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12,
  }) + ' ET';
}

function parsePayload(payloadJson: string | null): Record<string, unknown> {
  if (!payloadJson) return {};
  try {
    return JSON.parse(payloadJson) as Record<string, unknown>;
  } catch {
    return {};
  }
}

function statusIcon(payload: Record<string, unknown>): string {
  if (payload.success === true) return '✅';
  if (payload.retryAfter != null) return '⚠️';
  return '❌';
}

function HistoryEntry({
  entry,
  channelName,
  hour12,
}: {
  entry: AuditLogRow;
  channelName: string;
  hour12: boolean;
}) {
  const payload = parsePayload(entry.payloadJson);
  const icon = statusIcon(payload);
  const outcome = (payload.discordMessageId as string | undefined)
    ?? (payload.error as string | undefined)
    ?? (payload.message as string | undefined)
    ?? '—';

  return (
    <li className="flex items-start gap-2 py-1 text-sm">
      <span aria-label={icon === '✅' ? 'success' : icon === '⚠️' ? 'rate-limited' : 'failure'}>
        {icon}
      </span>
      <span className="text-muted-foreground shrink-0">{formatTs(entry.ts, hour12)}</span>
      <span className="text-muted-foreground shrink-0">#{channelName}</span>
      <span className="truncate font-mono text-xs">{outcome}</span>
    </li>
  );
}

export function ReminderHistoryPanel({ reminderId }: ReminderHistoryPanelProps) {
  const { data, isLoading } = useReminderHistory(reminderId);
  const { data: channels = [] } = useChannels();
  const { hour12 } = useTimeFormat();

  function channelNameFor(channelId: string | undefined): string {
    if (!channelId) return 'unknown channel';
    const ch = channels.find((c) => c.id === channelId);
    return ch ? ch.name : 'unknown channel';
  }

  if (isLoading) {
    return (
      <div className="space-y-2 py-2 px-4">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
        <Skeleton className="h-4 w-2/3" />
      </div>
    );
  }

  const entries = data?.entries ?? [];

  if (entries.length === 0) {
    return (
      <p className="text-muted-foreground py-2 px-4 text-sm">No posts yet</p>
    );
  }

  return (
    <ul className="divide-y py-1 px-4">
      {entries.map((entry) => {
        const payload = parsePayload(entry.payloadJson);
        const channelId = payload.channelId as string | undefined;
        return (
          <HistoryEntry
            key={entry.id}
            entry={entry}
            channelName={channelNameFor(channelId)}
            hour12={hour12}
          />
        );
      })}
    </ul>
  );
}
